import Dexie, { Table } from 'dexie';

export interface LocalHabit {
  id?: number;
  uuid: string;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  repeatDays: {
    mon: boolean; tue: boolean; wed: boolean; thu: boolean;
    fri: boolean; sat: boolean; sun: boolean;
  };
  goalType: 'continuous' | 'weekly';
  weeklyGoal?: number;
  displayOrder: number;
  isArchived: number;
  currentStreak: number;
  bestStreak: number;
  totalCompleted: number;
  reminderEnabled: number;
  reminderTime: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface LocalRecord {
  id?: number;
  uuid: string;
  habitUuid: string;
  date: string;
  status: 'pending' | 'completed' | 'skipped' | 'failed';
  value?: number;
  note?: string;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface AppSettings {
  id: string; // 'current'
  language: 'ja' | 'en';
  soundEnabled: boolean;
  theme: 'light' | 'dark' | 'auto';
  lastOpened: Date;
  hasPromptedPWA: boolean;
  firstVisitDone: boolean;
}

export interface UserStats {
  id: string; // 'current'
  userId: string;
  totalCompleted: number;
  currentStreak: number;
  longestStreak: number;
  perfectWeeks: number;
  perfectMonths: number;
  habitsCount: number;
  totalDays: number;
  joinedDate: Date;
  earlyBirdCount: number;
  nightOwlCount: number;
  weekendCount: number;
}

export interface BadgeState {
  id: string;
  isUnlocked: boolean;
  unlockedAt?: Date;
  progress: number;
}

class HabitTrackerDB extends Dexie {
  habits!: Table<LocalHabit>;
  records!: Table<LocalRecord>;
  badges!: Table<BadgeState>;
  stats!: Table<UserStats>;
  settings!: Table<AppSettings>;

  constructor() {
    super('HabitTrackerDB');
    
    // スキーマ定義
    const schema = {
      habits: '++id, uuid, displayOrder, isArchived',
      records: '++id, uuid, habitUuid, date, status, [habitUuid+date]',
      badges: 'id, isUnlocked',
      stats: 'id',
      settings: 'id'
    };

    // バージョン 7: isArchived/reminderEnabled の boolean → number 正規化、records の status 補完
    this.version(7).stores(schema).upgrade(async tx => {
      await tx.table('habits').toCollection().modify((habit: any) => {
        if (typeof habit.isArchived === 'boolean') {
          habit.isArchived = habit.isArchived ? 1 : 0;
        }
        if (typeof habit.reminderEnabled === 'boolean') {
          habit.reminderEnabled = habit.reminderEnabled ? 1 : 0;
        }
      });
      await tx.table('records').toCollection().modify((record: any) => {
        // 旧形式は status がなく completed: boolean だった場合の補完
        if (!record.status) {
          record.status = record.completed ? 'completed' : 'pending';
        }
      });
    });

    // バージョン 6: records に status インデックスを追加
    this.version(6).stores(schema);

    // バージョン 5: 以前のマイグレーション
    this.version(5).stores({
      habits: '++id, uuid, displayOrder, isArchived',
      records: '++id, uuid, habitUuid, date, [habitUuid+date]',
      badges: 'id, isUnlocked',
      stats: 'id',
      settings: 'id'
    }).upgrade(async tx => {
      // 古いバージョンから移行する場合、日付文字列を Date オブジェクトに変換
      await tx.table('habits').toCollection().modify(habit => {
        if (typeof habit.createdAt === 'string') habit.createdAt = new Date(habit.createdAt);
        if (typeof habit.updatedAt === 'string') habit.updatedAt = new Date(habit.updatedAt);
      });
      await tx.table('records').toCollection().modify(record => {
        if (typeof record.createdAt === 'string') record.createdAt = new Date(record.createdAt);
        if (typeof record.updatedAt === 'string') record.updatedAt = new Date(record.updatedAt);
        if (typeof record.completedAt === 'string') record.completedAt = new Date(record.completedAt);
      });
      await tx.table('stats').toCollection().modify(stat => {
        if (typeof stat.joinedDate === 'string') stat.joinedDate = new Date(stat.joinedDate);
      });
      await tx.table('settings').toCollection().modify(setting => {
        if (typeof setting.lastOpened === 'string') setting.lastOpened = new Date(setting.lastOpened);
      });
    });
  }
}

export const db = new HabitTrackerDB();

export const initDB = async (): Promise<void> => {
  try {
    if (!db.isOpen()) {
      await db.open();
    }
    
    // デフォルト設定の投入（存在しない場合のみ）
    const settings = await db.settings.get('current');
    if (!settings) {
      await db.settings.put({
        id: 'current',
        language: 'ja',
        soundEnabled: true,
        theme: 'auto',
        lastOpened: new Date(),
        hasPromptedPWA: false,
        firstVisitDone: false
      });
    }

    // 統計の初期値投入
    const stats = await db.stats.get('current');
    if (!stats) {
      await db.stats.put({
        id: 'current',
        userId: `local-${Date.now()}`,
        totalCompleted: 0,
        currentStreak: 0,
        longestStreak: 0,
        perfectWeeks: 0,
        perfectMonths: 0,
        habitsCount: 0,
        totalDays: 0,
        joinedDate: new Date(),
        earlyBirdCount: 0,
        nightOwlCount: 0,
        weekendCount: 0
      });
    }
  } catch (error) {
    console.error('[DB] Initialization error:', error);
    throw error;
  }
};

export const clearLocalData = async () => {
  try {
    // 全テーブルをクリア
    await Promise.all([
      db.habits.clear(),
      db.records.clear(),
      db.badges.clear(),
      db.stats.clear(),
      db.settings.clear()
    ]);
    
    // クリア後に初期値を再投入
    await initDB();
    
    console.log('[DB] Local data cleared and re-initialized');
  } catch (error) {
    console.error('[DB] Failed to clear local data:', error);
    throw error;
  }
};
